import React from "react";
import moment from "moment";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { Box, IconButton, Tooltip, Typography } from "@material-ui/core";
import AbtcAvatar from "../../custom/Avatar";
import NumberFormat from "../../custom/Number";
import wpp from "../../static/whatsapp.png";
import UserTagStatus from "./UserTagStatus";
import { capitalCaseNormalizer, isAgent, Permissions, phoneFormatter, Roles, userDocumentFormatter } from "../../util";
import DoneIcon from "@material-ui/icons/Done";
import useUserBeneficiary from "../../hooks/useUserBeneficiary";
import InfoItem from "../../custom/InfoItem";
import InfoIcon from "@material-ui/icons/Info";
import ActionDialog from "./ActionDialog";

const UserProfile = ({ currentUser, enableProfilePicture, user }) => {
  const { beneficiary } = useUserBeneficiary({ userId: user.id });
  const [showBeneficiaryDialog, setShowBeneficiaryDialog] = React.useState(false);

  const roleLabel =
    user.role === Roles.Agent ? <FormattedHTMLMessage id={`app.enuns.levelName.${user.contractAgent.contractAgentClass.levelName}`} /> : <FormattedHTMLMessage id={`app.enuns.${user.role}`} />;

  return (
    <>
      {enableProfilePicture && (
        <Box
          display="flex"
          justifyContent="center"
          marginBottom={4}
        >
          <AbtcAvatar
            imageUrl={user.photoUrl}
            disableLink
            size={120}
          />
        </Box>
      )}

      {/* nome */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        {user.id}
        {" - "}
        {roleLabel}
        <UserTagStatus
          variant="caption"
          apt={user.apt}
          isBlocked={user.isBlocked}
          premium={user.premium}
        />
      </Typography>
      <Typography
        variant="body1"
        color={user.premium ? "primary" : undefined}
        paragraph
      >
        {user.name}
      </Typography>

      {/* telefones */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.phone" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        {!user.telefone && !user.telefone2 ? (
          <FormattedMessage id="app.empty" />
        ) : (
          <>
            {user.telefone && (
              <>
                {phoneFormatter(user.telefone)}{" "}
                {user.isWhats && (
                  <a
                    href={"https://wa.me/55" + user.telefone}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={wpp}
                      width="14"
                      height="14"
                      alt=" "
                    />
                  </a>
                )}
              </>
            )}
            {user.telefone2 && (
              <>
                {phoneFormatter(user.telefone2)}{" "}
                {user.isWhats2 && (
                  <a
                    href={"https://wa.me/55" + user.telefone2}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={wpp}
                      width="14"
                      height="14"
                      alt=" "
                    />
                  </a>
                )}
              </>
            )}
          </>
        )}
      </Typography>
      {/* email */}
      {!user.emailConfirmed ? (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.email" />{" "}
            <span style={{ color: "red" }}>
              <FormattedMessage id="app.not.confirmed" />
            </span>
          </Typography>
          <Typography
            paragraph
            variant="body1"
          >
            {user.email}
          </Typography>
        </>
      ) : (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.email" />
          </Typography>
          <Typography
            paragraph
            variant="body1"
          >
            {user.email}
            <IconButton
              size="small"
              disabled
              style={{ color: "green" }}
            >
              <DoneIcon />
            </IconButton>
          </Typography>
        </>
      )}

      {/* domicilio fiscal */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.taxResidence" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        <FormattedMessage id={`app.taxResidence.${user.taxResidence}`} />
      </Typography>

      {/* documentos */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id={`app.enuns.${user.documentType}`} />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        {userDocumentFormatter(user, <FormattedMessage id="app.empty" />)}
      </Typography>
      {user.isPj && (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.legalRepresentativeName" />
          </Typography>
          <Typography
            paragraph
            variant="body1"
          >
            {user.legalRepresentativeName ? user.legalRepresentativeName : <FormattedMessage id="app.empty" />}
          </Typography>

          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.legalRepresentativeDocumentId" />
          </Typography>
          <Typography
            paragraph
            variant="body1"
          >
            {user.legalRepresentativeId ?? <FormattedMessage id="app.empty" />}
          </Typography>
        </>
      )}
      {!user.isPj && (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.dateOfBirth" />
          </Typography>
          <Typography
            paragraph
            variant="body1"
          >
            {user.dateOfBirth ? moment(user.dateOfBirth).format("DD/MM/YYYY") : <FormattedMessage id="app.empty" />}
          </Typography>
        </>
      )}

      {/* gênero */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.gender" />
      </Typography>
      <Typography
        variant="body1"
        paragraph
      >
        {user?.gender ? <FormattedMessage id={`app.gender.${user.gender}`} /> : <FormattedMessage id="app.notAvailable" />}
      </Typography>

      {/* endereço */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.address" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        {user.address ? user.address : "N/D"}, {user.city ? user.city : "N/D"} - {user.state ? user.state : "N/D"} - {user.country ? user.country : "N/D"}
      </Typography>

      {/* data de cadastro */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.dateRegister" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        {moment(user.createdAt).format("DD/MM/YYYY")}
      </Typography>

      {/* idioma */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.language" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        <FormattedMessage id={`app.language.${user.locale}`} />
      </Typography>

      {/* perfil */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.role" />
      </Typography>
      <Typography
        paragraph
        variant="body1"
      >
        {roleLabel}
      </Typography>

      {/* status do service agreement */}
      {user.role === Roles.Agent &&
        (user.contractAgent ? (
          <>
            {user.contractAgent.signatureDate ? (
              <>
                <Typography
                  gutterBottom
                  color="textSecondary"
                  variant="caption"
                >
                  <FormattedHTMLMessage id="app.contractAgent" />
                </Typography>
                <Typography>
                  <FormattedMessage id={`app.enuns.${user.contractAgent.status}`} />
                  {" - "}
                  {user.contractAgent.status === "ACTIVE" && (
                    <>
                      <FormattedMessage id="app.contractAgent.signed" />
                      {moment(user.contractAgent.signatureDate).format("DD/MM/YYYY")}
                    </>
                  )}
                </Typography>
              </>
            ) : (
              <>
                <Typography
                  gutterBottom
                  color="textSecondary"
                  variant="caption"
                >
                  <FormattedHTMLMessage id="app.contractAgent" />
                </Typography>
                <Typography>
                  <FormattedMessage id={`app.enuns.${user.contractAgent.status}`} />
                </Typography>
              </>
            )}
            {isAgent(user) && (
              <Typography variant="caption">
                Comisison limit:{" "}
                <NumberFormat
                  value={1 - user.contractAgent.splitForManager}
                  display="percentage"
                />
              </Typography>
            )}
            <Typography
              paragraph
              variant="body1"
            >
              {/*espaçador*/}
            </Typography>
          </>
        ) : (
          <>
            <Typography
              gutterBottom
              color="textSecondary"
              variant="caption"
            >
              <FormattedHTMLMessage id="app.contractAgent" />
            </Typography>
            <Typography
              paragraph
              variant="body1"
              color="error"
            >
              <b>
                <FormattedMessage id="app.none" />
              </b>
            </Typography>
          </>
        ))}

      {/* agente */}
      {user.consultant && (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            {user.consultant.id} {" - "}
            <FormattedMessage id="app.agent.assigned" />
          </Typography>
          <div
            color="textSecondary"
            style={{ display: "flex" }}
          >
            <Typography
              paragraph
              color="textPrimary"
              style={{ alignSelf: "center", textDecoration: "none" }}
              component={
                currentUser.role === Roles.Admin ||
                currentUser.role === Roles.BusinessManager ||
                currentUser.role === Roles.Auditor ||
                currentUser.role === Roles.AgentsManager
                  ? Link
                  : null
              }
              to={`/user/${user.consultant.id}`}
            >
              {capitalCaseNormalizer(user.consultant.name)}
            </Typography>
          </div>
        </>
      )}

      {/* recomendante */}
      <Typography color="textSecondary" variant="caption">
        {user?.recommender?.id && `${user.recommender.id} - `}
        <FormattedMessage id="app.recommender.assigned" />
      </Typography>
      {user.recommender ? (
        <Box display="flex">
          <Typography
            paragraph
            color="textPrimary"
            style={{ alignSelf: "center", textDecoration: "none" }}
            component={
              [Roles.Admin, Roles.BusinessManager, Roles.Auditor].includes(
                currentUser.role,
              )
                ? Link
                : null
            }
            to={`/user/${user.recommender.id}`}
          >
            {capitalCaseNormalizer(user.recommender.name)}{" "}
            <small>
              (
              <NumberFormat
                value={user.recommenderPercent}
                display="percentage"
              />
            </small>
            )
          </Typography>
          <div></div>
        </Box>
      ) : (
        <>
          <div color="textSecondary" style={{ display: "flex" }}>
            <Typography
              paragraph
              color="textPrimary"
              style={{ alignSelf: "center", textDecoration: "none" }}
            >
              <FormattedMessage id="app.none" />
            </Typography>
          </div>
        </>
      )}

      {/* beneficiário */}
      <Typography
        color="textSecondary"
        variant="caption"
      >
        <FormattedMessage id="app.beneficiary" />
      </Typography>
      {beneficiary ? (
        <Box
          display="flex"
          alignItems="center"
          gap={4}
        >
          <Typography color="textPrimary">{capitalCaseNormalizer(beneficiary.name)}</Typography>
          <IconButton
            size="small"
            onClick={() => {
              setShowBeneficiaryDialog(true);
            }}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Typography
          paragraph
          color="textPrimary"
          style={{ alignSelf: "center", textDecoration: "none" }}
        >
          <FormattedMessage id="app.none" />
        </Typography>
      )}

      {/* observações */}
      {[Roles.Admin, Roles.Kyc, Roles.FinanceManager].includes(currentUser.role) && (
        <>
          <Typography
            color="textSecondary"
            variant="caption"
          >
            <FormattedMessage id="app.obs" />
          </Typography>
          <Typography
            paragraph
            style={{ whiteSpace: "pre-line" }}
          >
            {user.observations ?? "N/A"}
          </Typography>
        </>
      )}

      {beneficiary && (
        <ActionDialog
          title="app.beneficiary"
          open={showBeneficiaryDialog}
          enableConfirmButton={false}
          maxWidth="xs"
          onClose={() => setShowBeneficiaryDialog(false)}
        >
          <InfoItem
            caption={<FormattedHTMLMessage id="app.relationship" />}
            text={beneficiary.relationship}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.name" />}
            text={beneficiary.name}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.email" />}
            text={beneficiary.email}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.phone" />}
            text={beneficiary.phone}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.address" />}
            text={beneficiary.address}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.documentType" />}
            text={beneficiary.documentType}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.documentId" />}
            text={beneficiary.documentId}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.dateOfBirth" />}
            text={beneficiary.dateOfBirth}
          />
          <InfoItem
            caption={<FormattedHTMLMessage id="app.obs" />}
            text={beneficiary.observations ?? "(N/A)"}
          />
        </ActionDialog>
      )}
    </>
  );
};

export default UserProfile;
